import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { AnalysisResult, BulkAnalysisResult } from '../types';

export interface ExportData {
  url: string;
  analysisResult: string; // "Safe/Unsafe" or "Matches Audience/No Match"
  confidence: string;
  reasoning: string;
  contentSummary: string;
  keywordBlocked: string;
  matchedKeywords: string;
  totalKeywordMatches: number;
  keywordReasoning: string;
  agreementStatus: string;
  provider: string;
  model: string;
  analyzedAt: string;
}

/**
 * Transforms analysis results into export-friendly format
 */
export const transformResultsForExport = (results: AnalysisResult[], isAudienceTargeting: boolean = false): ExportData[] => {
  return results.map((result, index) => {
    const keywordBlocked = result.keywordBlocking?.isBlocked || false;
    const aiSafe = result.isBrandSafe;
    
    // Agreement logic differs by context
    const agreement = isAudienceTargeting
      ? aiSafe === keywordBlocked  // For audience: both should agree on positive match
      : aiSafe === !keywordBlocked; // For brand safety: safe content should not be blocked

    return {
      url: result.url,
      analysisResult: isAudienceTargeting 
        ? (aiSafe ? 'Matches Audience' : 'No Match')
        : (aiSafe ? 'Match' : 'No Match'),
      confidence: `${(result.confidence * 100).toFixed(1)}%`,
      reasoning: result.reasoning,
      contentSummary: result.contentSummary,
      keywordBlocked: isAudienceTargeting
        ? (keywordBlocked ? 'Keywords Match' : 'No Keywords Match')
        : (keywordBlocked ? 'Blocked' : 'Allowed'),
      matchedKeywords: result.keywordBlocking?.matchedKeywords?.join(', ') || '',
      totalKeywordMatches: result.keywordBlocking?.totalMatches || 0,
      keywordReasoning: result.keywordBlocking?.reasoning || 'N/A',
      agreementStatus: agreement ? 'Agreement' : 'Disagreement',
      provider: result.provider || 'N/A',
      model: result.model || 'N/A',
      analyzedAt: new Date(result.analyzedAt).toLocaleString()
    };
  });
};

/**
 * Exports analysis results as CSV file
 */
export const exportToCSV = (results: AnalysisResult[], filename?: string, isAudienceTargeting: boolean = false) => {
  const exportData = transformResultsForExport(results, isAudienceTargeting);
  
  // Create CSV headers based on analysis type
  const headers = [
    'URL',
    isAudienceTargeting ? 'Audience Match' : 'Match Status',
    'Confidence',
    'AI Reasoning',
    'Content Summary',
    isAudienceTargeting ? 'Keywords Match' : 'Keyword Blocked',
    'Matched Keywords',
    'Total Keyword Matches',
    'Keyword Reasoning',
    'Agreement Status',
    'AI Provider',
    'AI Model',
    'Analyzed At'
  ];

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...exportData.map(row => [
      `"${row.url.replace(/"/g, '""')}"`,
      row.analysisResult,
      row.confidence,
      `"${row.reasoning.replace(/"/g, '""')}"`,
      `"${row.contentSummary.replace(/"/g, '""')}"`,
      row.keywordBlocked,
      `"${row.matchedKeywords.replace(/"/g, '""')}"`,
      row.totalKeywordMatches,
      `"${row.keywordReasoning.replace(/"/g, '""')}"`,
      row.agreementStatus,
      row.provider,
      row.model,
      `"${row.analyzedAt}"`
    ].join(','))
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const analysisType = isAudienceTargeting ? 'audience-analysis' : 'brand-safety-analysis';
  const defaultFilename = filename || `${analysisType}-${new Date().toISOString().split('T')[0]}.csv`;
  saveAs(blob, defaultFilename);
};

/**
 * Exports analysis results as Excel file
 */
export const exportToExcel = (results: AnalysisResult[], bulkResult?: BulkAnalysisResult, filename?: string, isAudienceTargeting: boolean = false) => {
  const exportData = transformResultsForExport(results, isAudienceTargeting);

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // Create main results worksheet
  const resultsWorksheet = XLSX.utils.json_to_sheet(exportData, {
    header: [
      'url',
      'analysisResult',
      'confidence',
      'reasoning',
      'contentSummary',
      'keywordBlocked',
      'matchedKeywords',
      'totalKeywordMatches',
      'keywordReasoning',
      'agreementStatus',
      'provider',
      'model',
      'analyzedAt'
    ]
  });

  // Set column headers based on analysis type
  XLSX.utils.sheet_add_aoa(resultsWorksheet, [[
    'URL',
    isAudienceTargeting ? 'Audience Match' : 'Brand Safe',
    'Confidence',
    'AI Reasoning',
    'Content Summary',
    isAudienceTargeting ? 'Keywords Match' : 'Keyword Blocked',
    'Matched Keywords',
    'Total Keyword Matches',
    'Keyword Reasoning',
    'Agreement Status',
    'AI Provider',
    'AI Model',
    'Analyzed At'
  ]], { origin: 'A1' });

  // Auto-size columns
  const columnWidths = [
    { wch: 50 }, // URL
    { wch: 12 }, // Brand Safe
    { wch: 12 }, // Confidence
    { wch: 40 }, // AI Reasoning
    { wch: 40 }, // Content Summary
    { wch: 15 }, // Keyword Blocked
    { wch: 30 }, // Matched Keywords
    { wch: 10 }, // Total Matches
    { wch: 30 }, // Keyword Reasoning
    { wch: 15 }, // Agreement
    { wch: 12 }, // Provider
    { wch: 15 }, // Model
    { wch: 20 }  // Analyzed At
  ];
  resultsWorksheet['!cols'] = columnWidths;

  // Add results worksheet
  XLSX.utils.book_append_sheet(workbook, resultsWorksheet, 'Analysis Results');

  // Create summary worksheet if bulk result is provided
  if (bulkResult) {
    const summaryData = [
      ['Analysis Summary', ''],
      ['Total URLs', bulkResult.summary.totalUrls],
      ['Matched URLs', bulkResult.summary.matchedUrls],
      ['Unmatched URLs', bulkResult.summary.unmatchedUrls],
      ['Match Percentage', `${((bulkResult.summary.matchedUrls / bulkResult.summary.totalUrls) * 100).toFixed(1)}%`],
      ['Average Confidence', `${(bulkResult.summary.averageConfidence * 100).toFixed(1)}%`],
      ['Analysis Date', new Date(bulkResult.analyzedAt).toLocaleString()],
      ['', ''],
      ['Agreement Analysis', ''],
      ['URLs with Agreement', results.filter(r => r.isBrandSafe === !(r.keywordBlocking?.isBlocked || false)).length],
      ['URLs with Disagreement', results.filter(r => r.isBrandSafe !== !(r.keywordBlocking?.isBlocked || false)).length],
      ['Agreement Percentage', `${((results.filter(r => r.isBrandSafe === !(r.keywordBlocking?.isBlocked || false)).length / results.length) * 100).toFixed(1)}%`]
    ];

    const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
    summaryWorksheet['!cols'] = [{ wch: 25 }, { wch: 20 }];
    
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');
  }

  // Generate Excel file and download
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
  const analysisType = isAudienceTargeting ? 'audience-analysis' : 'brand-safety-analysis';
  const defaultFilename = filename || `${analysisType}-${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(data, defaultFilename);
};

/**
 * Exports filtered results based on current pagination
 */
export const exportCurrentPage = (
  allResults: AnalysisResult[], 
  currentPage: number, 
  resultsPerPage: number, 
  format: 'csv' | 'excel',
  bulkResult?: BulkAnalysisResult,
  isAudienceTargeting: boolean = false
) => {
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const pageResults = allResults.slice(startIndex, endIndex);
  
  const analysisType = isAudienceTargeting ? 'audience-analysis' : 'brand-safety-analysis';
  const filename = `${analysisType}-page-${currentPage}-${new Date().toISOString().split('T')[0]}`;
  
  if (format === 'csv') {
    exportToCSV(pageResults, `${filename}.csv`, isAudienceTargeting);
  } else {
    exportToExcel(pageResults, undefined, `${filename}.xlsx`, isAudienceTargeting);
  }
};

/**
 * Exports all results
 */
export const exportAllResults = (
  results: AnalysisResult[], 
  format: 'csv' | 'excel',
  bulkResult?: BulkAnalysisResult,
  isAudienceTargeting: boolean = false
) => {
  if (format === 'csv') {
    exportToCSV(results, undefined, isAudienceTargeting);
  } else {
    exportToExcel(results, bulkResult, undefined, isAudienceTargeting);
  }
};